/** Decisions for issue #422 open questions (encoded for implementation):
 * 1. Single PS PATCH with nested `homeInspectionAttempt` (API creates linked HI attempt in-transaction).
 * 2. Vacant & Abandoned gated by case type id `VAI`.
 * 3. Linked HI work order id from `workOrder.linkedHIWorkOrderID`.
 * 4. On failure: surface API error; never toast full success.
 *
 * Decisions for issue #424 (edit):
 * 1. PS PATCH `update[]` for Process Service fields (nested HI is create-only on the API).
 * 2. Separate `PATCH /home-inspection/.../attempts/{id}` for HI fields.
 * 3. Prefill from `GET /work-orders/{id}/attempt` enrichment (same as view).
 */

export const VACANT_ABANDONED_CASE_TYPE_ID = 'VAI';

export const PS_FIRST_ATTEMPT_HI_TITLE = (workOrderNumber: string) =>
  `W/O: ${workOrderNumber} - Add Status Update`;

export const PS_FIRST_ATTEMPT_HI_VIEW_TITLE = (workOrderNumber: string) =>
  `W/O: ${workOrderNumber} - Status Update`;

export const PS_FIRST_ATTEMPT_HI_EDIT_TITLE = (workOrderNumber: string) =>
  `W/O: ${workOrderNumber} - Edit Status Update`;

export const PS_FIRST_ATTEMPT_HI_SUBTITLE = 'This Service Includes Home Inspection';
export type PsFirstAttemptHiStepId =
  | 'serviceInformation'
  | 'ownerInformation'
  | 'tenantInformation'
  | 'propertyInformation'
  | 'vacancyChecklist';

export type PsFirstAttemptHiStepConfig = {
  id: PsFirstAttemptHiStepId;
  title: string;
};

const BASE_EXPANDED_STEPS: PsFirstAttemptHiStepConfig[] = [
  { id: 'serviceInformation', title: 'Service Information' },
  { id: 'ownerInformation', title: "Owner's Information" },
  { id: 'tenantInformation', title: "Tenant's Information" },
  { id: 'propertyInformation', title: 'Property Information' },
];

const VACANCY_STEP: PsFirstAttemptHiStepConfig = {
  id: 'vacancyChecklist',
  title: 'Vacancy Checklist',
};

export type PsFirstAttemptHiEligibilityInput = {
  existingAttemptCount: number;
  linkedHIWorkOrderID: string | null | undefined;
  caseTypeId: string | null | undefined;
};

export const isVacantAndAbandonedCase = (caseTypeId: string | null | undefined): boolean =>
  caseTypeId === VACANT_ABANDONED_CASE_TYPE_ID;

/** Resolve PS case type id from work-order payload (typeId or nested type.caseTypeId). */
export const resolvePsCaseTypeId = (input: {
  typeId?: string | null;
  type?: { caseTypeId?: string | null; name?: string | null } | null;
} | null | undefined): string | null => {
  if (!input) return null;
  if (input.typeId) return input.typeId;
  if (input.type?.caseTypeId) return input.type.caseTypeId;
  if (input.type?.name?.toLowerCase().includes('vacant') && input.type?.name?.toLowerCase().includes('abandon')) {
    return VACANT_ABANDONED_CASE_TYPE_ID;
  }
  return null;
};

export const includesHomeInspection = (linkedHIWorkOrderID: string | null | undefined): boolean =>
  Boolean(linkedHIWorkOrderID);

export const isFirstAttempt = (existingAttemptCount: number): boolean => existingAttemptCount === 0;

/** True when the expanded PS+HI first-attempt stepper should be used. */
export const shouldUsePsFirstAttemptHiStepper = (input: PsFirstAttemptHiEligibilityInput): boolean =>
  isFirstAttempt(input.existingAttemptCount) && includesHomeInspection(input.linkedHIWorkOrderID);

export const getPsFirstAttemptHiSteps = (
  input: Pick<PsFirstAttemptHiEligibilityInput, 'caseTypeId'>
): PsFirstAttemptHiStepConfig[] => {
  if (isVacantAndAbandonedCase(input.caseTypeId)) {
    return [...BASE_EXPANDED_STEPS, VACANCY_STEP];
  }
  return [...BASE_EXPANDED_STEPS];
};

export const getPsFirstAttemptHiHeader = (workOrderNumber: string) => ({
  title: PS_FIRST_ATTEMPT_HI_TITLE(workOrderNumber),
  subtitle: PS_FIRST_ATTEMPT_HI_SUBTITLE,
});

export const getPsFirstAttemptHiViewHeader = (workOrderNumber: string) => ({
  title: PS_FIRST_ATTEMPT_HI_VIEW_TITLE(workOrderNumber),
  subtitle: PS_FIRST_ATTEMPT_HI_SUBTITLE,
});

export const getPsFirstAttemptHiEditHeader = (workOrderNumber: string) => ({
  title: PS_FIRST_ATTEMPT_HI_EDIT_TITLE(workOrderNumber),
  subtitle: PS_FIRST_ATTEMPT_HI_SUBTITLE,
});

const isFirstLinkedHiAttemptTarget = (input: {
  attemptId: string | null;
  workOrderAttempts: { attemptId: string }[] | undefined;
  homeInspectionAttempts: unknown[] | undefined;
  linkedHIWorkOrderID?: string | null;
}): boolean => {
  const hasLinkedSignal =
    input.homeInspectionAttempts !== undefined || Boolean(input.linkedHIWorkOrderID);
  if (!hasLinkedSignal) return false;
  const first = input.workOrderAttempts?.[0];
  if (!first) return false;
  if (input.attemptId && input.attemptId !== first.attemptId) return false;
  return true;
};

/** True when viewing the first PS attempt that has linked HI enrichment on the attempt GET. */
export const shouldUsePsFirstAttemptHiView = (input: {
  mode: string | null;
  attemptId: string | null;
  workOrderAttempts: { attemptId: string }[] | undefined;
  /** `undefined` = unlinked enrichment; array (possibly empty) = linked enrichment present. */
  homeInspectionAttempts: unknown[] | undefined;
  /** Same linked signal as the side panel (WO detail). Used when enrichment key is absent. */
  linkedHIWorkOrderID?: string | null;
}): boolean => {
  if (input.mode !== 'view') return false;
  return isFirstLinkedHiAttemptTarget(input);
};

/**
 * True when editing the first PS attempt with linked HI via the expanded stepper.
 * Requires `attemptId` of the first attempt (view→edit). Bulk `mode=edit` without attemptId
 * keeps the generic multi-attempt form.
 *
 * Save paths differ from create on purpose: create nests `homeInspectionAttempt` on a single
 * PS PATCH; edit uses dedicated HI PATCH then PS `update[]` (nested HI is create-only on the API).
 */
export const shouldUsePsFirstAttemptHiEdit = (input: {
  mode: string | null;
  attemptMode: string | null;
  attemptId: string | null;
  workOrderAttempts: { attemptId: string }[] | undefined;
  homeInspectionAttempts: unknown[] | undefined;
  linkedHIWorkOrderID?: string | null;
}): boolean => {
  if (input.mode !== 'edit') return false;
  if (input.attemptMode === 'create') return false;
  if (!input.attemptId) return false;
  if (!input.homeInspectionAttempts?.length) return false;
  return isFirstLinkedHiAttemptTarget(input);
};
